<template lang="pug">
button.sticker-collection-grid-item(
  type="button"
  :class="rootClasses"
  @click="handleClick"
  @mouseenter="emitHint(true)"
  @mouseleave="emitHint(false)"
  @focus="emitHint(true)"
  @blur="emitHint(false)"
  @touchstart.passive="onTouchStart"
  @touchend.passive="onTouchEnd"
  @touchcancel.passive="onTouchEnd"
)
  .slot-image-wrap
    img.slot-image(
      v-if="imageSrc && !imageFailed"
      :src="imageSrc"
      :alt="item.sticker.title"
      @error="onImageError"
    )
    .slot-placeholder(v-else) {{ placeholderInitial }}
    .collected-badge(v-if="item.collected")
      img(src="@/assets/icons/check.svg" alt="")
  span.slot-tooltip(v-if="inlineTooltip" aria-hidden="true") {{ tooltipText }}
  span.slot-label {{ item.sticker.title }}
  span.slot-unavailable(v-if="unavailable") Unavailable
  span.slot-clue(v-if="large && !item.collected && item.clue") {{ item.clue }}
</template>

<script lang="ts">
import {
  computed, defineComponent, onUnmounted, ref,
} from 'vue';
import type { StickerCollectionItem } from '@/data/stickerCollection.types';
import isStickerUnavailable, { unavailableStickerText } from '@/utils/stickerCollectionSticker';

export default defineComponent({
  name: 'StickerCollectionGridItem',
  props: {
    item: {
      type: Object as () => StickerCollectionItem,
      required: true,
    },
    isCurrent: {
      type: Boolean,
      default: false,
    },
    /** Full-width tile for the collection page grid; shows the clue under locked stickers. */
    large: {
      type: Boolean,
      default: false,
    },
    /** Circular 44px tile used inside the profile achievements panel. */
    compact: {
      type: Boolean,
      default: false,
    },
    /** Off inside horizontally scrolling rows, where the tooltip would be clipped. */
    inlineTooltip: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['select', 'hint'],
  setup(props, { emit }) {
    const imageFailed = ref(false);
    const showTouchTooltip = ref(false);
    let touchTooltipTimer: ReturnType<typeof window.setTimeout> | null = null;

    // Full image first: some stickers on dev share a stale thumbnailUrl.
    const imageSrc = computed(
      () => props.item.sticker.url || props.item.sticker.thumbnailUrl || '',
    );

    const placeholderInitial = computed(() => {
      const title = props.item.sticker.title?.trim();
      return title ? title.charAt(0).toUpperCase() : '?';
    });

    // A draft/archived sticker has no public page, so its tile can't be opened.
    const unavailable = computed(
      () => !props.item.collected && isStickerUnavailable(props.item.sticker),
    );

    const rootClasses = computed(() => ({
      'is-unavailable': unavailable.value,
      'is-collected': props.item.collected,
      'is-current': props.isCurrent,
      'is-compact': props.compact,
      'is-large': props.large,
      'tooltip-visible': showTouchTooltip.value,
    }));

    const tooltipText = computed(() => {
      if (props.item.collected) {
        return 'Collected ✓';
      }
      if (unavailable.value) {
        return unavailableStickerText(props.item.sticker);
      }
      if (props.item.clue?.trim()) {
        return props.item.clue.trim();
      }
      return 'Tap to unlock';
    });

    function onImageError() {
      imageFailed.value = true;
    }

    function emitHint(active: boolean) {
      emit('hint', active ? tooltipText.value : null);
    }

    function handleClick() {
      if (unavailable.value) {
        emitHint(true);
        return;
      }
      emit('select', props.item.sticker);
    }

    function clearTouchTooltipTimer() {
      if (touchTooltipTimer != null) {
        window.clearTimeout(touchTooltipTimer);
        touchTooltipTimer = null;
      }
    }

    function onTouchStart() {
      if (props.item.collected) {
        return;
      }
      clearTouchTooltipTimer();
      showTouchTooltip.value = true;
      emitHint(true);
    }

    function onTouchEnd() {
      clearTouchTooltipTimer();
      touchTooltipTimer = window.setTimeout(() => {
        showTouchTooltip.value = false;
        touchTooltipTimer = null;
        emitHint(false);
      }, 2500);
    }

    onUnmounted(() => {
      clearTouchTooltipTimer();
    });

    return {
      rootClasses,
      unavailable,
      imageSrc,
      imageFailed,
      placeholderInitial,
      tooltipText,
      showTouchTooltip,
      onImageError,
      emitHint,
      handleClick,
      onTouchStart,
      onTouchEnd,
    };
  },
});
</script>

<style lang="scss" scoped>
.sticker-collection-grid-item {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  width: 3.75rem;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  position: relative;

  &:focus-visible {
    outline: 2px solid #0d9488;
    outline-offset: 2px;
    border-radius: 0.75rem;
  }
}

.slot-image-wrap {
  position: relative;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 1rem;
  overflow: visible;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.sticker-collection-grid-item.is-collected:hover .slot-image-wrap {
  transform: scale(1.05);
}

.sticker-collection-grid-item.is-collected .slot-image-wrap {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.slot-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 1rem;
  transition: filter 0.2s ease, opacity 0.2s ease;
}

.sticker-collection-grid-item:not(.is-collected) .slot-image {
  filter: grayscale(100%);
  opacity: 0.3;
}

.sticker-collection-grid-item:not(.is-collected):hover .slot-image,
.sticker-collection-grid-item:not(.is-collected):focus-visible .slot-image {
  opacity: 0.45;
}

.sticker-collection-grid-item.is-current .slot-image-wrap {
  box-shadow: 0 0 0 1px #fff, 0 0 0 3px #0d9488;
  border-radius: 1rem;
}

.slot-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 1rem;
  background: #ebebeb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: #9ca3af;
}

.sticker-collection-grid-item:not(.is-collected) .slot-placeholder {
  opacity: 0.3;
}

.collected-badge {
  position: absolute;
  bottom: -0.25rem;
  right: -0.25rem;
  width: 1.25rem;
  height: 1.25rem;
  background: #0d9488;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);

  img {
    width: 0.5625rem;
    height: 0.5625rem;
    filter: brightness(0) invert(1);
  }
}

.slot-tooltip {
  pointer-events: none;
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  width: 8rem;
  padding: 0.375rem 0.625rem;
  background: #1a2744;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  color: #fff;
  font-size: 0.5625rem;
  font-weight: 600;
  line-height: 1.375;
  text-align: center;
  border-radius: 0.5rem;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    width: 0.5rem;
    height: 0.5rem;
    margin-top: -0.25rem;
    background: #1a2744;
    border-radius: 0.125rem;
    transform: translateX(-50%) rotate(45deg);
  }
}

.sticker-collection-grid-item:hover .slot-tooltip,
.sticker-collection-grid-item:focus-visible .slot-tooltip,
.sticker-collection-grid-item.tooltip-visible .slot-tooltip {
  opacity: 1;
  visibility: visible;
}

.slot-label {
  font-size: 0.5625rem;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  width: 100%;
  color: #6b7280;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.sticker-collection-grid-item:not(.is-collected) .slot-label {
  color: #d1d5db;
}

.sticker-collection-grid-item.is-unavailable {
  cursor: default;
}

.slot-unavailable {
  margin-top: -0.125rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.5rem;
  font-weight: 800;
  color: #9ca3af;
}

// Profile achievements panel: 44px circles, 8px labels
.sticker-collection-grid-item.is-compact {
  width: 2.75rem;
  gap: 0.25rem;

  .slot-image-wrap {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 50%;
  }

  .slot-image,
  .slot-placeholder {
    border-radius: 50%;
  }

  .collected-badge {
    bottom: -0.125rem;
    right: -0.125rem;
    width: 1rem;
    height: 1rem;
    border: 2px solid #f9fafb;
    box-shadow: none;

    img {
      width: 0.4375rem;
      height: 0.4375rem;
    }
  }

  .slot-label {
    font-size: 0.5rem;
  }
}

// Collection page grid: tiles fill their grid cell
.sticker-collection-grid-item.is-large {
  width: 100%;
  gap: 0.5rem;
  align-items: stretch;
  padding: 0.375rem;
  margin: -0.375rem;

  .slot-image-wrap {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
    border-radius: 1.25rem;
  }

  .slot-image,
  .slot-placeholder {
    border-radius: 1.25rem;
  }

  .collected-badge {
    bottom: -0.375rem;
    right: -0.375rem;
    width: 1.5rem;
    height: 1.5rem;

    img {
      width: 0.75rem;
      height: 0.75rem;
    }
  }

  .slot-label {
    font-size: 0.8125rem;
  }

  &:not(.is-collected) .slot-label {
    color: #9ca3af;
  }

  .slot-clue {
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1.3;
    text-align: center;
    color: #9ca3af;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}
</style>
